import { format, formatDistanceToNow, subHours } from 'date-fns'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import {
  CountryComplianceRegulationResult,
  ValidationSubmissionFailureReason,
  ValidationSubmissionLog,
  ValidationSubmissionLogType
} from '@percent/admin-dashboard/api/actions/validationSubmissions/validationSubmissions.types'
import { LocaleKey } from '@percent/admin-dashboard/i18n/config'
import { Badge, Group, TimelineElement } from '@percent/lemonade'
import { UseValidationSubmissionTimelineProps } from './ValidationSubmissionTimeline.types'
import { Monitoring, SingleAdminValidationSubmission } from '@percent/domain/verifications'

export const validationSubmissionLogsToSkip: ValidationSubmissionLogType[] = [
  'fraud_check_advanced_domain_check_completed',
  'fraud_check_advanced_domain_check_requested',
  'fraud_check_application_check_completed',
  'fraud_check_application_check_requested',
  'fraud_check_basic_domain_check_completed',
  'fraud_check_basic_domain_check_requested',
  'fraud_check_document_check_completed',
  'fraud_check_document_check_requested',
  'fraud_review_ralph_recommendation_received'
]

type LogOfType<T extends ValidationSubmissionLog['type']> = Extract<ValidationSubmissionLog, { type: T }>

const formatStatus = (t: TFunction, status?: string | null) => {
  switch (status) {
    case 'pass':
      return t('typography.passed')
    case 'fail':
      return t('typography.failed')
    case 'pending_review':
      return t('typography.pendingReview')
    case 'pending_user_verification':
      return t('typography.pendingUserVerification')
    default:
      return status
  }
}

const isFlaggedResult = (value: unknown): boolean =>
  Boolean(value && typeof value === 'object' && 'status' in value && (value as { status: string }).status === 'flag')

const getMonitoringFlaggedDescription = (
  t: TFunction,
  results: NonNullable<Monitoring['results']> | undefined
): string => {
  const flaggedKeys = results
    ? Object.keys(results).filter(key => isFlaggedResult(results[key as keyof typeof results]))
    : []

  return (
    flaggedKeys.length === 0
      ? t('typography.noFlags')
      : t('typography.flaggedChecks', {
          flags: flaggedKeys.map(key => t(`form.${key}` as LocaleKey)).join(', ')
        })
  ) as string
}

const formatCountryComplianceRegulations = (
  t: TFunction,
  regulations: CountryComplianceRegulationResult[] | undefined
): string | null => {
  if (!regulations?.length) return null

  return regulations
    .map(({ regulation, reasonCode }) => {
      const regulationLabel = t(`typography.timeline.countryCompliance.regulation.${regulation}` as LocaleKey, {
        defaultValue: regulation
      }) as string
      const reasonLabel = t(`typography.timeline.countryCompliance.reasonCode.${reasonCode}` as LocaleKey, {
        defaultValue: reasonCode
      }) as string

      return `${regulationLabel} (${reasonLabel})`
    })
    .join(', ')
}

const getEligibilityResultChange = (t: TFunction, log: LogOfType<'eligibility_result_change'>): TimelineElement => {
  const result = log.data.updatedCheck?.result
  const tags = log.data.updatedCheck?.results?.confirmedActivitySubTags

  return {
    title: t('typography.timeline.eligibilityResultChange.title', { result: formatStatus(t, result) }),
    description: result ? (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {t('typography.timeline.eligibilityResultChange.description')}
        {tags?.map((tag: { id: string; name: string }) => (
          <Badge variant="default" key={tag.id}>
            {tag.name}
          </Badge>
        ))}
      </div>
    ) : (
      t('status.cantDefine')
    )
  }
}

const getMonitoringSubscriptionResultChange = (
  t: TFunction,
  log: LogOfType<'monitoring_subscription_result_change'>
): TimelineElement => {
  const results = log.data.updatedCheck?.results

  return {
    title: t('typography.timeline.monitoringSubscriptionResultChange.title', {
      result: formatStatus(t, log.data.updatedCheck?.result)
    }),
    description: getMonitoringFlaggedDescription(t, results)
  }
}

const getCountryComplianceCheckResultChange = (
  t: TFunction,
  log: LogOfType<'country_compliance_check_result_change'>
): TimelineElement => {
  const { updatedCheck } = log.data

  return {
    title: t('typography.timeline.countryComplianceCheckResultChange.title', {
      result: formatStatus(t, updatedCheck?.result)
    }),
    description: formatCountryComplianceRegulations(t, updatedCheck?.results?.regulations)
  }
}

const getAgentVerificationStatusChange = (
  t: TFunction,
  log: LogOfType<'agent_verification_status_change'>
): TimelineElement => {
  const { updatedCheck } = log.data

  let description: string | null = null

  if (
    updatedCheck?.status === 'rejected' &&
    'rejectionReasonCode' in updatedCheck &&
    updatedCheck.rejectionReasonCode
  ) {
    description = t(`dropdown.reject.${updatedCheck.rejectionReasonCode}` as LocaleKey)
  } else if (
    updatedCheck?.status === 'approved' &&
    'approvalReasonCode' in updatedCheck &&
    updatedCheck.approvalReasonCode
  ) {
    description = t(`dropdown.approve.${updatedCheck.approvalReasonCode}` as LocaleKey)
  }

  return {
    title: t('typography.timeline.agentVerificationStatusChange.title', {
      status: formatStatus(t, updatedCheck?.status)
    }),
    description
  }
}

const getFailureReasonDescription = (t: TFunction, failureReason: ValidationSubmissionFailureReason): JSX.Element => {
  const checkLabel = t(
    `typography.timeline.validationSubmissionFailed.check.${failureReason.check}` as LocaleKey
  ) as string

  if (failureReason.check === 'validation_request') {
    if (!failureReason.reason.rejectionReasonCode) {
      return <div>{checkLabel}</div>
    }

    const reasonLabel = t(
      `typography.timeline.status.${failureReason.reason.rejectionReasonCode}` as LocaleKey
    ) as string

    return <div>{`${checkLabel}: ${reasonLabel}`}</div>
  }

  if (failureReason.check === 'agent_verification') {
    if (!failureReason.reason.rejectionReasonCode) {
      return <div>{checkLabel}</div>
    }

    const reasonLabel = t(`dropdown.reject.${failureReason.reason.rejectionReasonCode}` as LocaleKey) as string

    return <div>{`${checkLabel}: ${reasonLabel}`}</div>
  }

  if (failureReason.check === 'eligibility') {
    if (failureReason.reason.status === 'cannot_define_eligibility') {
      return <div>{`${checkLabel}: ${t('status.cantDefine') as string}`}</div>
    }

    const tags = failureReason.reason.results.rejectedActivitySubTags

    if (tags?.length) {
      return (
        <Group gap={8} wrap="wrap">
          {`${checkLabel}:`}
          {tags.map((tag: { id: string; name: string }) => (
            <Badge variant="default" key={tag.id}>
              {tag.name}
            </Badge>
          ))}
        </Group>
      )
    }

    return <div>{checkLabel}</div>
  }

  if (failureReason.check === 'compliance') {
    const reasonLabel = getMonitoringFlaggedDescription(t, failureReason.reason.results)

    return <div>{`${checkLabel}: ${reasonLabel}`}</div>
  }

  if (failureReason.check === 'country_compliance') {
    const failedRegulations = failureReason.reason.results?.regulations?.filter(({ outcome }) => outcome !== 'pass')
    const reasonLabel = formatCountryComplianceRegulations(t, failedRegulations)

    return <div>{reasonLabel ? `${checkLabel}: ${reasonLabel}` : checkLabel}</div>
  }

  return <div>{checkLabel}</div>
}

const getValidationSubmissionFailed = (
  t: TFunction,
  log: LogOfType<'validation_submission_failed'>
): TimelineElement => {
  const title = t('typography.timeline.validationSubmissionFailed.title')

  if (!log.data?.failureReasons?.length) {
    return { title }
  }

  const description = (
    <div>
      {log.data.failureReasons.map(failureReason => (
        <div key={failureReason.check}>{getFailureReasonDescription(t, failureReason)}</div>
      ))}
    </div>
  )

  return { title, description }
}

const getTimelineElement = (
  t: TFunction,
  log: ValidationSubmissionLog,
  validationSubmission: SingleAdminValidationSubmission
): TimelineElement => {
  switch (log.type) {
    case 'eligibility_result_change':
      return getEligibilityResultChange(t, log)
    case 'monitoring_subscription_result_change':
      return getMonitoringSubscriptionResultChange(t, log)
    case 'agent_verification_status_change':
      return getAgentVerificationStatusChange(t, log)
    case 'country_compliance_check_result_change':
      return getCountryComplianceCheckResultChange(t, log)
    case 'validation_request_accepted':
      return {
        title: t('typography.timeline.validationRequestAccepted.title'),
        description: log.data?.accountEmail ? t('typography.by', { author: String(log.data.accountEmail) }) : undefined
      }
    case 'validation_request_rejected':
      return {
        title: t('typography.timeline.validationRequestRejected.title'),
        description: (
          <div>
            {log.data?.accountEmail && <div>{t('typography.by', { author: String(log.data.accountEmail) })}</div>}
            <div>
              {t(
                `typography.timeline.status.${validationSubmission.validationRequest?.rejectionReasonCode}` as LocaleKey
              )}
            </div>
          </div>
        )
      }
    case 'validation_submission_processed':
      return {
        title: t('typography.timeline.validationSubmissionProcessed.title'),
        description: t('typography.timeline.validationSubmissionProcessed.description')
      }
    case 'fraud_check_completed':
      return {
        title: t('typography.timeline.fraudCheckCompleted.title'),
        description: t(`typography.timeline.fraudCheckCompleted.${log.data.fraudCheckResult}`)
      }
    case 'validation_submission_failed':
      return getValidationSubmissionFailed(t, log)
    case 'validation_submission_failed_outcome_email_sent':
      return { title: t('typography.timeline.validationSubmissionFailedOutcomeEmail.title') }
    case 'validation_request_accepted_by_auto_merge':
      return { title: t('typography.timeline.validationRequestAutoMerged.title') }
    case 'validation_submission_succeeded':
      return { title: t('typography.timeline.validationSubmissionSucceeded.title') }
    case 'validation_submission_succeeded_outcome_email_sent':
      return { title: t('typography.timeline.validationSubmissionSucceededOutcomeEmail.title') }
    case 'validation_submission_pending':
      return { title: t('typography.timeline.validationSubmissionPending.title') }
    case 'fraud_check_marked_as_safe':
      return {
        title: log.data?.reusedFromValidationSubmissionId
          ? t('typography.timeline.fraudCheckMarkedAsSafeReused.title')
          : t('typography.timeline.fraudCheckMarkedAsSafe.title'),
        description: log.data?.accountEmail ? t('typography.by', { author: String(log.data.accountEmail) }) : undefined
      }
    case 'fraud_check_requested':
      return { title: t('typography.timeline.fraudCheckRequested.title') }
    default:
      return { title: t('typography.timeline.unknownStatus') }
  }
}

const formatTimestamp = (createdAt: Date | string) => {
  const date = new Date(createdAt)
  const oneHourAgo = subHours(new Date(), 1)

  return date < oneHourAgo ? format(date, 'yyyy-MM-dd HH:mm') : formatDistanceToNow(date, { addSuffix: true })
}

export const useValidationSubmissionTimeline = ({
  logs,
  validationSubmission
}: UseValidationSubmissionTimelineProps) => {
  const { t } = useTranslation()

  if (!logs) return []

  return [...logs]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .filter(log => !validationSubmissionLogsToSkip.includes(log.type))
    .map(log => ({
      ...getTimelineElement(t, log, validationSubmission),
      additionalDescription: formatTimestamp(log.createdAt)
    }))
}
